import { ChangeDetectionStrategy, Component, computed, EventEmitter, HostBinding, Input, Output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoPipe } from '@jsverse/transloco';

import { PageEvent } from './paginator.model';
import { IconDirective } from '../icon/icon.directive';
import { SelectItemsComponent } from '../select/select-content.component';
import { SelectOptionComponent } from '../select/select-option.component';
import { SelectComponent } from '../select/select.component';

@Component({
  standalone: true,
  selector: 'tt-paginator',
  templateUrl: './paginator.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SelectComponent, SelectItemsComponent, SelectOptionComponent, FormsModule, TranslocoPipe, MatIconModule, IconDirective],
})
export class PaginatorComponent {
  @HostBinding('class') public readonly class = 'tt-paginator';

  @Input() public pageSizeOptions = [5, 10, 25, 100];

  protected _itemsPerPage = signal(10);
  @Input() public set itemsPerPage(itemsPerPage: number) {
    this._itemsPerPage.set(itemsPerPage);
  }

  protected _page = signal(1);
  @Input() public set page(pageIndex: number) {
    this._page.set(pageIndex);
  }

  protected _maxElements = signal(0);
  @Input() public set maxElements(maxElements: number) {
    this._maxElements.set(maxElements);
  }

  @Output() public pageEvent = new EventEmitter<PageEvent>();

  protected numberOfPages = computed(() => {
    const itemsPerPage = this._itemsPerPage();
    const maxElements = this._maxElements();
    return itemsPerPage > 0 ? Math.ceil(maxElements / itemsPerPage) : 0;
  });

  protected handleFirstPageClicked() {
    if (this._page() === 1) {
      return;
    }

    this._page.set(1);
    this.handlePageClicked();
  }

  protected handleLastPageClicked() {
    if (this._page() >= this.numberOfPages()) {
      return;
    }

    this._page.set(this.numberOfPages());
    this.handlePageClicked();
  }

  protected handleNextPageClicked() {
    if (this._page() >= this.numberOfPages()) {
      return;
    }

    this._page.update((page) => page + 1);
    this.handlePageClicked();
  }

  protected handlePreviousPageClicked() {
    if (this._page() === 1) {
      return;
    }

    this._page.set(this._page() - 1);
    this.handlePageClicked();
  }

  public handlePageSizeChanged($event: number) {
    this._itemsPerPage.set($event);
    this._page.set(1);

    this.handlePageClicked();
  }

  private handlePageClicked() {
    this.pageEvent.emit({
      page: this._page(),
      itemsPerPage: this._itemsPerPage(),
    });
  }
}
